import type { ThemePageParentSelectFragment } from '~/api/graphql/fragments/themePage.generated';
import type { TemplateType } from './../types/template';
import type { IThemePageBase } from '~/types/themePage';

import { IThemePageStatus } from '~/types/themePage';
import { deepCopy } from '~/utils';
import { create } from 'zustand';

export interface IThemePage extends IThemePageBase {
  type: TemplateType;
  parent?: ThemePageParentSelectFragment;
}

export interface IThemePagesMap extends Record<string, IThemePage> {}

interface ThemePageStore {
  themePages?: IThemePage[];
  //Including draft theme pages
  allThemePagesMap: IThemePagesMap;
  idsFetched: string[];
  isReFetchedThemePages: boolean;
  setIsReFetchedThemePages: (isReFetchedThemePages: boolean) => void;
  setAllThemePagesMap: (map: IThemePagesMap) => void;
  upsertThemePages: (theme: IThemePage[]) => void;
  setThemePageDefaults: (theme: IThemePage[]) => void;
  getThemePages: () => IThemePage[] | undefined;
  setIdsFetched: (ids: string[]) => void;
  setThemePagePublished: (themePageId: string) => void;
  clear: () => void;
}

export const useThemePageStore = create<ThemePageStore>()((set, get) => ({
  themePages: undefined,
  allThemePagesMap: {},
  isReFetchedThemePages: true,
  setIsReFetchedThemePages: (isReFetchedThemePages) => set({ isReFetchedThemePages }),
  setAllThemePagesMap: (map) => set({ allThemePagesMap: map }),
  upsertThemePages: (newThemePages: IThemePage[]) =>
    set((state) => {
      const currentThemePages = deepCopy(state.themePages) || [];
      const updatedThemePages = [...currentThemePages];

      newThemePages.forEach((newThemePage) => {
        const index = updatedThemePages.findIndex((page) => page.id === newThemePage.id);
        if (index >= 0) {
          updatedThemePages[index] = { ...updatedThemePages[index], ...newThemePage };
        } else {
          updatedThemePages.push(newThemePage);
        }
      });

      return {
        ...state,
        themePages: updatedThemePages,
      };
    }),

  setThemePageDefaults: (newThemePages: IThemePage[]) =>
    set((state) => {
      const currentThemePages = deepCopy(state.themePages) || [];
      const newThemePageIds = new Set(newThemePages.map((page) => page.id));

      // Reset old defaults to false if not in the new list
      const updatedThemePages = currentThemePages.map((page) =>
        page.default && !newThemePageIds.has(page.id) ? { ...page, default: false } : page,
      );

      // Upsert new default pages
      newThemePages.forEach((newPage) => {
        const index = updatedThemePages.findIndex((page) => page.id === newPage.id);
        if (index >= 0) {
          updatedThemePages[index] = { ...updatedThemePages[index], ...newPage };
        } else {
          updatedThemePages.push(newPage);
        }
      });

      return {
        ...state,
        themePages: updatedThemePages,
      };
    }),
  getThemePages: () => get().themePages,
  idsFetched: [],
  setIdsFetched: (ids) => set({ idsFetched: [...get().idsFetched, ...ids] }),
  setThemePagePublished: (themePageId) =>
    set((state) => {
      if (!state.themePages) return state;

      const updatedThemePages = state.themePages.map((page) =>
        page.id === themePageId ? { ...page, status: IThemePageStatus.PUBLISHED } : page,
      );
      return { ...state, themePages: updatedThemePages };
    }),
  clear: () => set({ themePages: undefined, allThemePagesMap: {}, idsFetched: [] }),
}));
